import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { JOURS, depBrutHeures, calculerTotalJour, formatDate, formatDateHeure } from '../../../utils/releveHeures';
import logoSrc from '../../../images/logo.svg';

const BRAND = [43, 108, 176];
const LIGHT_GRAY = [244, 247, 250];
const TEXT_DARK = [26, 32, 44];
const TEXT_MUTED = [113, 128, 150];
const AMBER = [214, 158, 46];
const BORDER = [220, 226, 232];

// Rasterise le logo (SVG) sur un canvas pour pouvoir l'intégrer dans le PDF
const chargerLogoDataUrl = () => new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
        const nw = img.naturalWidth || 200;
        const nh = img.naturalHeight || 90;
        const scale = 6;
        const canvas = document.createElement('canvas');
        canvas.width = nw * scale;
        canvas.height = nh * scale;
        const ctx = canvas.getContext('2d');
        ctx.scale(scale, scale);
        ctx.drawImage(img, 0, 0);
        resolve({ dataUrl: canvas.toDataURL('image/png'), ratio: nh / nw });
    };
    img.onerror = () => resolve(null);
    img.src = logoSrc;
});

const dessinerCase = (doc, x, y, coche, taille = 3.6) => {
    doc.setDrawColor(...TEXT_MUTED);
    doc.setLineWidth(0.25);
    doc.rect(x, y - taille, taille, taille);
    if (coche) {
        doc.setDrawColor(...BRAND);
        doc.setLineWidth(0.6);
        doc.line(x + 0.5, y - taille / 2, x + taille / 2.2, y - 0.5);
        doc.line(x + taille / 2.2, y - 0.5, x + taille - 0.4, y - taille + 0.4);
    }
    doc.setLineWidth(0.2);
};

// Génère et télécharge le PDF "Annexe 3" du relevé hebdomadaire du temps de travail
export const exporterReleveHeuresPDF = async ({
    monday, sunday, details, totalSemaine, totalSemaineBrut, totalDeplacementsBruts, heuresSup,
    autorisationDir, reposRespectes, remarquesRepos, signatureName, semaine, annee,
    dateSignature, adresseIp, empreinteSha256
}) => {
    const doc = new jsPDF({ unit: 'mm', format: 'a4' });
    const pageWidth = doc.internal.pageSize.getWidth();
    const marginX = 14;
    const logo = await chargerLogoDataUrl();

    // ── Bandeau d'en-tête ──
    doc.setFillColor(...BRAND);
    doc.rect(0, 0, pageWidth, 30, 'F');
    if (logo) {
        const logoWidth = 26;
        doc.addImage(logo.dataUrl, 'PNG', marginX, 6, logoWidth, logoWidth * logo.ratio);
    }
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(15);
    doc.setFont(undefined, 'bold');
    doc.text('RELEVÉ HEBDOMADAIRE DU TEMPS DE TRAVAIL', pageWidth - marginX, 13, { align: 'right' });
    doc.setFontSize(9);
    doc.setFont(undefined, 'normal');
    doc.text('Annexe 3', pageWidth - marginX, 19, { align: 'right' });
    doc.text(`Semaine ${semaine} / ${annee}  -  Du ${formatDate(monday)} au ${formatDate(sunday)}`, pageWidth - marginX, 25, { align: 'right' });

    let y = 40;
    doc.setTextColor(...TEXT_DARK);
    doc.setFontSize(10);
    doc.setFont(undefined, 'bold');
    doc.text(`Salarié : ${signatureName || '.....................................'}`, marginX, y);
    doc.setFont(undefined, 'normal');
    doc.text('Horaire contractuel : 39 heures / semaine', pageWidth - marginX, y, { align: 'right' });
    y += 6;

    // ── Tableau des jours ──
    const tableData = JOURS.map(jour => {
        const d = details[jour.toLowerCase()];
        const total = calculerTotalJour(d);
        const dep = depBrutHeures(d.depBrut);
        return [jour, d.debut || '—', d.fin || '—', d.pause || '—', dep ? `${dep} h` : '—', `${total} h`, d.obs || ''];
    });

    doc.autoTable({
        startY: y,
        margin: { left: marginX, right: marginX },
        head: [['Jour', 'Début', 'Fin', 'Pause', 'Trajet', 'Total', 'Observations']],
        body: tableData,
        theme: 'grid',
        styles: { fontSize: 9, cellPadding: 2.4, lineColor: BORDER, lineWidth: 0.2, textColor: TEXT_DARK },
        headStyles: { fillColor: BRAND, textColor: 255, fontStyle: 'bold', halign: 'center' },
        alternateRowStyles: { fillColor: LIGHT_GRAY },
        columnStyles: {
            0: { fontStyle: 'bold', cellWidth: 24 },
            1: { halign: 'center', cellWidth: 18 },
            2: { halign: 'center', cellWidth: 18 },
            3: { halign: 'center', cellWidth: 18 },
            4: { halign: 'center', cellWidth: 20 },
            5: { halign: 'center', cellWidth: 18, fontStyle: 'bold' },
            6: { cellWidth: 'auto' }
        }
    });

    y = doc.lastAutoTable.finalY + 8;

    // ── Bandeau récapitulatif ──
    const recapWidth = pageWidth - marginX * 2;
    doc.setFillColor(...LIGHT_GRAY);
    doc.roundedRect(marginX, y, recapWidth, 24, 2, 2, 'F');

    const recapItems = [
        ['Total brut', `${totalSemaineBrut} h`],
        ['Déplacements', `${totalDeplacementsBruts} h`],
        ['Trajet déduit', '- 1h30'],
        ['Total net', `${totalSemaine} h`],
        ['Heures sup. (> 39h)', `${heuresSup} h`]
    ];
    const colWidth = recapWidth / recapItems.length;
    recapItems.forEach(([label, value], i) => {
        const cx = marginX + colWidth * i + colWidth / 2;
        doc.setFontSize(8);
        doc.setTextColor(...TEXT_MUTED);
        doc.text(label, cx, y + 8, { align: 'center' });
        doc.setFontSize(12.5);
        doc.setFont(undefined, 'bold');
        doc.setTextColor(...(i === 4 && parseFloat(heuresSup) > 0 ? AMBER : TEXT_DARK));
        doc.text(value, cx, y + 17, { align: 'center' });
        doc.setFont(undefined, 'normal');
    });
    doc.setTextColor(...TEXT_DARK);

    y += 24 + 10;

    // ── Validation et engagements ──
    doc.setFontSize(10.5);
    doc.setFont(undefined, 'bold');
    doc.text('Validation et engagements', marginX, y);
    doc.setFont(undefined, 'normal');
    doc.setFontSize(9);
    y += 7;

    doc.text('Autorisation préalable de la Direction pour le dépassement de 39h :', marginX, y);
    dessinerCase(doc, marginX + 128, y, autorisationDir === 'oui');
    doc.text('Oui', marginX + 133, y);
    dessinerCase(doc, marginX + 148, y, autorisationDir === 'non');
    doc.text('Non', marginX + 153, y);
    y += 8;

    doc.text('Repos quotidien (11h) et hebdomadaire (39h) respectés :', marginX, y);
    dessinerCase(doc, marginX + 128, y, reposRespectes === 'oui');
    doc.text('Oui', marginX + 133, y);
    dessinerCase(doc, marginX + 148, y, reposRespectes === 'non');
    doc.text('Non', marginX + 153, y);

    if (reposRespectes === 'non' && remarquesRepos) {
        y += 6;
        doc.setFontSize(8);
        doc.setTextColor(...TEXT_MUTED);
        doc.text(`Précisions : ${remarquesRepos}`, marginX, y);
        doc.setTextColor(...TEXT_DARK);
        doc.setFontSize(9);
    }

    y += 14;
    doc.setDrawColor(...BORDER);
    doc.line(marginX, y, pageWidth - marginX, y);
    y += 10;

    doc.setFontSize(9);
    doc.text(`Fait le ${formatDate(new Date())}`, marginX, y + 8);

    const sigBoxWidth = 60;
    const sigBoxHeight = 22;
    const sigBoxX = pageWidth - marginX - sigBoxWidth;
    const sigCenterX = sigBoxX + sigBoxWidth / 2;
    doc.setDrawColor(...BORDER);
    doc.roundedRect(sigBoxX, y, sigBoxWidth, sigBoxHeight, 2, 2);
    doc.setFontSize(8);
    doc.setTextColor(...TEXT_MUTED);
    doc.text('Signature du salarié', sigCenterX, y + 7, { align: 'center' });
    doc.setFontSize(11);
    doc.setFont(undefined, 'bold');
    doc.setTextColor(...TEXT_DARK);
    doc.text(signatureName || '.....................................', sigCenterX, y + 15, { align: 'center' });
    doc.setFont(undefined, 'normal');

    y += sigBoxHeight + 8;

    // ── Preuve électronique de signature (horodatage, IP, empreinte) ──
    if (dateSignature) {
        const proofHeight = 26;
        doc.setDrawColor(...BORDER);
        doc.setFillColor(...LIGHT_GRAY);
        doc.roundedRect(marginX, y, pageWidth - marginX * 2, proofHeight, 2, 2, 'FD');

        doc.setFontSize(8);
        doc.setFont(undefined, 'bold');
        doc.setTextColor(...BRAND);
        doc.text('PREUVE ÉLECTRONIQUE DE SIGNATURE', marginX + 4, y + 6);
        doc.setFont(undefined, 'normal');

        doc.setFontSize(8);
        doc.setTextColor(...TEXT_DARK);
        doc.text(`Horodatage : ${formatDateHeure(dateSignature)}     Adresse IP : ${adresseIp || 'non disponible'}`, marginX + 4, y + 13);

        const empreinteAffichee = empreinteSha256 ? `${empreinteSha256.slice(0, 24)}…${empreinteSha256.slice(-16)}` : 'non disponible';
        doc.setFontSize(7.5);
        doc.text(`Empreinte SHA-256 (témoin d'intégrité) : ${empreinteAffichee}`, marginX + 4, y + 19);

        doc.setFontSize(6.5);
        doc.setTextColor(...TEXT_MUTED);
        doc.text("Signature électronique valant preuve, conformément à l'article 1366 du Code civil.", marginX + 4, y + 24);
        doc.setTextColor(...TEXT_DARK);

        y += proofHeight + 6;
    }

    // ── Pied de page ──
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(7.5);
        doc.setTextColor(...TEXT_MUTED);
        doc.text(`ATC Joaillerie - Relevé d'heures S${semaine}/${annee}`, marginX, doc.internal.pageSize.getHeight() - 8);
        doc.text(`Page ${i}/${pageCount}`, pageWidth - marginX, doc.internal.pageSize.getHeight() - 8, { align: 'right' });
    }

    doc.save(`Releve_S${semaine}_${annee}.pdf`);
};

